import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { apiUrl, assetUrl } from '@/config/api';
import normalizeLanguageCode from '@/utils/language.js';
import { getDeckCardTextStyle } from '@/utils/deckTextColors.js';
import CardPreviewModal from '@/components/CardPreviewModal.jsx';
import InfoTooltip from '@/components/InfoTooltip.jsx';
import useLanguageNavigation from '@/hooks/useLanguageNavigation.js';
import { useAuth } from '../context/AuthContext.jsx';
import useGameReferences from '../hooks/useGameReferences.js';
import useTariffs from '../hooks/useTariffs.js';
import useWalletBalance from '../hooks/useWalletBalance.js';
import WalletBalanceSummary from '../components/WalletBalanceSummary.jsx';

const getBoardLanguages = (board) => {
  if (!board?.cellSchemes) {
    return [];
  }

  const languages = board.cellSchemes
    .map((binding) => (binding?.language || '').trim().toLowerCase())
    .filter(Boolean);

  return Array.from(new Set(languages));
};

const getBoardLanguageOptions = (boardList) => {
  if (!boardList?.length) {
    return [];
  }

  const allLanguages = boardList.flatMap((board) => getBoardLanguages(board));
  return Array.from(new Set(allLanguages));
};

const getBoardsByLanguage = (boardList, language) => {
  if (!boardList?.length) {
    return [];
  }

  const normalizedLanguage = normalizeLanguageCode(language);
  return boardList.filter((board) =>
    getBoardLanguages(board).includes(normalizedLanguage),
  );
};

const getPlayerLimit = (type, role = null) => {
  if (type === 'group') {
    return role === 'super' ? 20 : 6;
  }
  return 1;
};

const getPlayerMin = (type) => (type === 'group' ? 2 : 1);

const getDeckLanguages = (deckList) => {
  if (!deckList?.length) {
    return [];
  }

  const languages = deckList
    .map((deck) => normalizeLanguageCode(deck.language))
    .filter(Boolean);

  return Array.from(new Set(languages));
};

const getDecksByLanguage = (deckList, language) => {
  const normalizedLanguage = normalizeLanguageCode(language);
  return deckList.filter((deck) => normalizeLanguageCode(deck.language) === normalizedLanguage);
};

const getDefaultDeckForLanguage = (deckList, language) => {
  const normalizedLanguage = normalizeLanguageCode(language);
  return deckList.find(
    (deck) => deck.defaultForLanguage && normalizeLanguageCode(deck.language) === normalizedLanguage,
  );
};

const getPreferredDeckIdForLanguage = (deckList, language, fallbackDeckId = '') => {
  const decksForLanguage = getDecksByLanguage(deckList, language);
  const defaultDeck = getDefaultDeckForLanguage(deckList, language);
  if (defaultDeck) {
    return defaultDeck.id;
  }
  const isFallbackAvailable = decksForLanguage.some((deck) => String(deck.id) === String(fallbackDeckId));
  return isFallbackAvailable ? fallbackDeckId : decksForLanguage[0]?.id || '';
};

const getDeckBackgroundUrl = (deck) => deck?.cardBackgroundUrl || deck?.backgroundUrl || deck?.cardBack?.imageUrl || '';

const getLocalizedLanguageLabel = (language, t) => {
  const normalizedLanguage = normalizeLanguageCode(language);
  return t(`platform.languages.${normalizedLanguage}`, { ns: 'common' });
};

const BOARD_ROWS = 8;
const BOARD_COLS = 9;
const TOTAL_CELLS = BOARD_ROWS * BOARD_COLS;
const buildCreateRequestId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const CreateGamePage = ({ mode = 'create' }) => {
  const { t, i18n } = useTranslation(['game', 'common', 'dashboard']);
  const { id: routeGameId } = useParams();
  const [searchParams] = useSearchParams();
  const { token, user } = useAuth();
  const { navigate } = useLanguageNavigation();
  const { boards, decks, loading: refsLoading, error: refsError } = useGameReferences(token);
  const diceOptions = useMemo(
    () => [
      { value: 'auto', label: t('create.dice.auto') },
      { value: 'manual', label: t('create.dice.manual') },
    ],
    [t],
  );
  const typeTabs = useMemo(
    () => [
      { value: 'individual', label: t('create.typeTabs.individual') },
      { value: 'group', label: t('create.typeTabs.group') },
      { value: 'personal', label: t('create.typeTabs.personal') },
    ],
    [t],
  );
  const createPlayer = useCallback((name = '') => ({ name, diceType: 'auto', startCellInput: '' }), []);
  const getInitialPlayers = useCallback(
    (type) => {
      if (type === 'group') {
        return [createPlayer(), createPlayer()];
      }
      if (type === 'personal') {
        return [createPlayer()];
      }
      return [createPlayer()];
    },
    [createPlayer, t],
  );
  const getDefaultPlayerName = useCallback((index) => t('create.players.defaultName', { index: index + 1 }), [t]);
  const [activeType, setActiveType] = useState('individual');
  const [form, setForm] = useState({
    title: '',
    boardId: '',
    deckId: '',
    boardLanguage: '',
    deckLanguage: '',
  });
  const [players, setPlayers] = useState(() => getInitialPlayers('individual'));
  const [saving, setSaving] = useState(false);
  const submitInFlightRef = useRef(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [startCellErrors, setStartCellErrors] = useState({});
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [cardPreview, setCardPreview] = useState({ open: false, loading: false, card: null, error: '' });
  const [previewError, setPreviewError] = useState(false);
  const [insufficientFundsOpen, setInsufficientFundsOpen] = useState(false);
  const [createdGameType, setCreatedGameType] = useState(null);
  const [createdGameModalOpen, setCreatedGameModalOpen] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editUpdatedAt, setEditUpdatedAt] = useState('');
  const [initialPlayersCount, setInitialPlayersCount] = useState(0);
  const [pendingEditPayload, setPendingEditPayload] = useState(null);
  const [editConfirm, setEditConfirm] = useState({ open: false, delta: 0 });

  const isEditMode = mode === 'edit' && Number.isInteger(Number.parseInt(routeGameId || '', 10));
  const editGameId = isEditMode ? Number.parseInt(routeGameId, 10) : null;

  const editReturnTarget = useMemo(() => {
    const from = (searchParams.get('from') || '').trim().toLowerCase();
    if (from === 'future') {
      return 'future';
    }
    if (from === 'current') {
      return 'current';
    }
    return null;
  }, [searchParams]);

  const { gameCosts, loading: tariffsLoading, error: tariffsError } = useTariffs(token, {
    errorMessage: t('create.cost.error'),
  });

  const { walletBalance, walletLoading, walletError } = useWalletBalance(token, {
    errorMessage: t('billing.errors.wallet'),
  });

  const interfaceLanguage = useMemo(() => {
    const localeSource = i18n.resolvedLanguage || i18n.language || '';
    const normalizedLocale = String(localeSource).trim().toLowerCase();
    const baseLocale = normalizedLocale.split('-')[0];
    return normalizeLanguageCode(baseLocale || normalizedLocale);
  }, [i18n.language, i18n.resolvedLanguage]);

  useEffect(() => {
    if (refsLoading) {
      return;
    }

    setForm((prev) => {
      const boardLanguages = getBoardLanguageOptions(boards);
      const normalizedBoardLanguage = normalizeLanguageCode(
        prev.boardLanguage || interfaceLanguage || boardLanguages[0],
      );
      const nextBoardLanguage = boardLanguages.includes(normalizedBoardLanguage)
        ? normalizedBoardLanguage
        : boardLanguages[0] || normalizedBoardLanguage;

      const boardsForLanguage = getBoardsByLanguage(boards, nextBoardLanguage);
      const isBoardAvailable = boardsForLanguage.some((board) => String(board.id) === String(prev.boardId));
      const nextBoardId = isBoardAvailable
        ? prev.boardId
        : prev.boardId
          ? boardsForLanguage.length === 1
            ? boardsForLanguage[0]?.id || ''
            : ''
          : boardsForLanguage[0]?.id || '';

      const deckLanguages = getDeckLanguages(decks);
      const normalizedDeckLanguage = normalizeLanguageCode(
        prev.deckLanguage || interfaceLanguage || deckLanguages[0],
      );
      const nextDeckLanguage = deckLanguages.includes(normalizedDeckLanguage)
        ? normalizedDeckLanguage
        : deckLanguages[0] || normalizedDeckLanguage;

      const nextDeckId = getPreferredDeckIdForLanguage(decks, nextDeckLanguage, prev.deckId);

      return {
        ...prev,
        boardLanguage: nextBoardLanguage,
        boardId: nextBoardId,
        deckLanguage: nextDeckLanguage,
        deckId: nextDeckId,
      };
    });
  }, [boards, decks, interfaceLanguage, refsLoading]);

  useEffect(() => {
    if (isEditMode) {
      return;
    }
    setPlayers(getInitialPlayers(activeType));
    setStartCellErrors({});
  }, [activeType, getInitialPlayers, isEditMode]);

  useEffect(() => {
    if (!isEditMode || !token || !editGameId) {
      return;
    }

    let cancelled = false;
    const loadEditGame = async () => {
      try {
        setEditLoading(true);
        const response = await fetch(apiUrl(`/api/games/${editGameId}/edit`), {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data.error || t('list.errors.load'));
        }
        if (!data.canEdit) {
          throw new Error(data.reason || t('list.errors.save'));
        }
        if (cancelled) {
          return;
        }

        const game = data.game || {};
        const resolvePlayerName = (player) =>
          player?.name || player?.displayName || player?.title || player?.username || '';
        const normalizedPlayers = Array.isArray(game.players)
          ? game.players.map((player) => ({
              id: player.id || player.playerId || null,
              playerId: player.playerId || player.id || null,
              name: resolvePlayerName(player),
              diceType: player.diceType || 'auto',
              startCellInput: Number.isInteger(player.startCellNumber) ? String(player.startCellNumber) : '',
              token: player.token || null,
              chipKey: player.chipKey || null,
              pendingSixSum: player.pendingSixSum || 0,
              playerRequest: player.playerRequest || null,
              currentCell: player.currentCell || 1,
              nameMode: player.nameMode || 'preset',
            }))
          : [createPlayer(), createPlayer()];

        setActiveType('group');
        const matchedDeck = Array.isArray(decks)
          ? decks.find((deck) => String(deck.id) === String(game.deckId || ''))
          : null;
        setForm((prev) => ({
          ...prev,
          title: game.title || '',
          boardId: game.boardId || '',
          deckId: game.deckId || '',
          boardLanguage: game.fieldLanguage || prev.boardLanguage || '',
          deckLanguage: normalizeLanguageCode(matchedDeck?.language || prev.deckLanguage || ''),
        }));
        setPlayers(normalizedPlayers);
        setInitialPlayersCount(normalizedPlayers.length);
        setEditUpdatedAt(game.updatedAt || '');
      } catch (error) {
        if (!cancelled) {
          setErrorMessage(error.message || t('list.errors.load'));
        }
      } finally {
        if (!cancelled) {
          setEditLoading(false);
        }
      }
    };

    loadEditGame();
    return () => {
      cancelled = true;
    };
  }, [createPlayer, decks, editGameId, isEditMode, t, token]);

  const availableBoardLanguages = useMemo(() => getBoardLanguageOptions(boards), [boards]);

  const effectiveBoardLanguage = useMemo(() => {
    const normalizedLanguage = normalizeLanguageCode(form.boardLanguage);
    if (availableBoardLanguages.includes(normalizedLanguage)) {
      return normalizedLanguage;
    }
    return availableBoardLanguages[0] || '';
  }, [availableBoardLanguages, form.boardLanguage]);

  const boardsBySelectedLanguage = useMemo(
    () => getBoardsByLanguage(boards, effectiveBoardLanguage),
    [boards, effectiveBoardLanguage],
  );

  const selectedBoard = useMemo(
    () => boardsBySelectedLanguage.find((board) => String(board.id) === String(form.boardId)),
    [boardsBySelectedLanguage, form.boardId],
  );

  const availableDeckLanguages = useMemo(() => getDeckLanguages(decks), [decks]);

  const decksBySelectedLanguage = useMemo(
    () => getDecksByLanguage(decks, form.deckLanguage || availableDeckLanguages[0]),
    [decks, form.deckLanguage, availableDeckLanguages],
  );

  const selectedDeck = useMemo(
    () => decksBySelectedLanguage.find((deck) => String(deck.id) === String(form.deckId)),
    [decksBySelectedLanguage, form.deckId],
  );

  const previewImageUrl = useMemo(() => {
    if (!selectedBoard) {
      return '';
    }
    const backgroundUrl = selectedBoard.background?.imageUrl || selectedBoard.backgroundImageUrl;
    return backgroundUrl ? assetUrl(backgroundUrl) : '';
  }, [selectedBoard]);

  const deckPreviewBackground = useMemo(
    () => assetUrl(getDeckBackgroundUrl(selectedDeck)),
    [selectedDeck],
  );

  useEffect(() => {
    setPreviewError(false);
  }, [previewImageUrl]);

  const playersCount = players.length;

  const currentGameCost = useMemo(() => {
    if (!gameCosts) {
      return null;
    }

    if (activeType === 'individual') {
      return Number(gameCosts.individual) || 0;
    }

    if (activeType === 'personal') {
      return Number(gameCosts.personal) || 0;
    }

    const groupCost = gameCosts.group?.[playersCount];
    return Number(groupCost) || 0;
  }, [activeType, gameCosts, playersCount]);

  const isCostConfigured = currentGameCost !== null && currentGameCost > 0;
  const isCreateDisabled = saving || editLoading || refsLoading || tariffsLoading || tariffsError || !isCostConfigured;

  const shouldShowTariffMissingMessage = !tariffsLoading && !tariffsError && !isCostConfigured;

  const costDisplay = useMemo(() => {
    if (tariffsLoading) {
      return t('create.cost.loading');
    }
    if (tariffsError) {
      return t('create.cost.error');
    }
    if (currentGameCost === null) {
      return t('create.cost.unknown');
    }
    if (!isCostConfigured) {
      return null;
    }
    return t('create.cost.value', { cost: currentGameCost });
  }, [currentGameCost, isCostConfigured, t, tariffsError, tariffsLoading]);

  const playerLimit = useMemo(() => getPlayerLimit(activeType, user?.role), [activeType, user?.role]);
  const playerMin = useMemo(() => getPlayerMin(activeType), [activeType]);

  const handlePlayerChange = (index, field, value) => {
    setPlayers((prev) =>
      prev.map((player, idx) => {
        if (idx !== index) {
          return player;
        }
        return { ...player, [field]: value };
      }),
    );
  };

  const addPlayer = () => {
    if (players.length >= playerLimit) {
      return;
    }
    setPlayers((prev) => [...prev, createPlayer()]);
  };

  const removePlayer = (index) => {
    if (players.length <= playerMin) {
      return;
    }
    setPlayers((prev) => prev.filter((_, idx) => idx !== index));
    setStartCellErrors((prev) => {
      const next = {};
      Object.entries(prev).forEach(([key, value]) => {
        const parsedKey = Number(key);
        if (!Number.isInteger(parsedKey) || !value) {
          return;
        }
        if (parsedKey === index) {
          return;
        }
        const nextIndex = parsedKey > index ? parsedKey - 1 : parsedKey;
        next[nextIndex] = value;
      });
      return next;
    });
  };

  const handleBoardLanguageChange = (nextLanguage) => {
    setForm((prev) => {
      const boardsForLanguage = getBoardsByLanguage(boards, nextLanguage);
      const isBoardAvailable = boardsForLanguage.some((board) => String(board.id) === String(prev.boardId));
      return {
        ...prev,
        boardLanguage: nextLanguage,
        boardId: isBoardAvailable
          ? prev.boardId
          : prev.boardId
            ? boardsForLanguage.length === 1
              ? boardsForLanguage[0]?.id || ''
              : ''
            : boardsForLanguage[0]?.id || '',
      };
    });
  };

  const handleDeckLanguageChange = (nextLanguage) => {
    setForm((prev) => {
      const nextDeckId = getPreferredDeckIdForLanguage(decks, nextLanguage, prev.deckId);

      return {
        ...prev,
        deckLanguage: nextLanguage,
        deckId: nextDeckId,
      };
    });
  };

  const buildEditPayload = useCallback((preparedPlayers) => ({
    title: form.title.trim(),
    boardId: Number(form.boardId),
    deckId: Number(form.deckId),
    boardLanguage: form.boardLanguage,
    players: preparedPlayers,
    expectedUpdatedAt: editUpdatedAt,
  }), [editUpdatedAt, form.boardId, form.boardLanguage, form.deckId, form.title]);

  const submitEditUpdate = async (preparedPlayers) => {
    const editPayload = buildEditPayload(preparedPlayers);
    const repriceResponse = await fetch(apiUrl(`/api/games/${editGameId}/reprice`), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ players: preparedPlayers }),
    });
    const repriceData = await repriceResponse.json().catch(() => ({}));
    if (!repriceResponse.ok) {
      console.error('[GAME_EDIT_REPRICE_ERROR]', repriceData);
      throw new Error(repriceData.error || t('list.errors.save'));
    }

    if ((Number(repriceData.delta) || 0) > 0) {
      setPendingEditPayload(editPayload);
      setEditConfirm({ open: true, delta: Number(repriceData.delta) || 0 });
      return;
    }

    const updateResponse = await fetch(apiUrl(`/api/games/${editGameId}/update`), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(editPayload),
    });
    const updateData = await updateResponse.json().catch(() => ({}));
    if (!updateResponse.ok) {
      console.error('[GAME_EDIT_UPDATE_ERROR]', updateData);
      throw new Error(updateData.error || t('list.errors.save'));
    }
    setEditUpdatedAt(updateData?.game?.updatedAt || editUpdatedAt);
    setCreatedGameType('group');
    setCreatedGameModalOpen(true);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (saving || editLoading || submitInFlightRef.current) {
      return;
    }
    setErrorMessage('');
    setStartCellErrors({});
    if (!form.title.trim()) {
      setErrorMessage(t('create.messages.titleRequired'));
      return;
    }
    if (!form.boardId || !form.deckId) {
      setErrorMessage(t('create.messages.boardAndDeckRequired'));
      return;
    }
    const nextStartCellErrors = {};
    const preparedPlayers = players.map((player, index) => {
      const rawName = (player.name || '').trim();
      const resumeValue = player.startCellInput?.trim?.() || '';
      let startCellNumber = null;
      if (resumeValue) {
        const parsed = Number(resumeValue);
        if (!Number.isInteger(parsed)) {
          nextStartCellErrors[index] = t('create.messages.resumeCellInvalid');
        } else if (parsed < 1 || parsed > TOTAL_CELLS) {
          nextStartCellErrors[index] = t('create.messages.resumeCellRange', { max: TOTAL_CELLS });
        } else {
          startCellNumber = parsed;
        }
      }
      return {
        ...(isEditMode ? { id: player.id || player.playerId || null } : {}),
        ...(activeType === 'personal' ? {} : { name: rawName }),
        diceType: player.diceType,
        ...(startCellNumber ? { startCellNumber } : {}),
        ...(player.token ? { token: player.token } : {}),
        ...(player.chipKey ? { chipKey: player.chipKey } : {}),
        ...(player.pendingSixSum ? { pendingSixSum: player.pendingSixSum } : {}),
        ...(player.playerRequest ? { playerRequest: player.playerRequest } : {}),
        ...(player.currentCell ? { currentCell: player.currentCell } : {}),
        ...(player.nameMode ? { nameMode: player.nameMode } : {}),
      };
    });
    if (Object.keys(nextStartCellErrors).length > 0) {
      setStartCellErrors(nextStartCellErrors);
      return;
    }
    if (isEditMode && preparedPlayers.length < initialPlayersCount) {
      setErrorMessage(t('list.actions.editDecreaseForbidden'));
      return;
    }
    if (preparedPlayers.length < playerMin || preparedPlayers.length > playerLimit) {
      setErrorMessage(t('create.messages.playerCountInvalid'));
      return;
    }
    if (tariffsLoading) {
      setErrorMessage(t('create.messages.waitForTariffs'));
      return;
    }
    if (tariffsError) {
      setErrorMessage(t('create.messages.tariffsError'));
      return;
    }
    if (!isCostConfigured) {
      setErrorMessage(t('create.messages.tariffMissing'));
      return;
    }
    submitInFlightRef.current = true;
    try {
      setSaving(true);
      if (isEditMode) {
        await submitEditUpdate(preparedPlayers);
      } else {
        const requestId = buildCreateRequestId();
        const response = await fetch(apiUrl('/api/games'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            type: activeType,
            title: form.title.trim(),
            boardId: Number(form.boardId),
            deckId: Number(form.deckId),
            boardLanguage: form.boardLanguage,
            players: preparedPlayers,
            requestId,
          }),
        });
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          if (errorData.errorCode === 'INSUFFICIENT_FUNDS') {
            setInsufficientFundsOpen(true);
            return;
          }
          if (errorData.field === 'startCellNumber') {
            setStartCellErrors((prev) => ({
              ...prev,
              [errorData.index]: errorData.error || t('create.messages.resumeCellInvalid'),
            }));
            return;
          }
          throw new Error(errorData.error || t('create.messages.createError'));
        }
        setCreatedGameType(activeType);
        setCreatedGameModalOpen(true);
      }
    } catch (submitError) {
      setErrorMessage(submitError.message || t('create.messages.createError'));
    } finally {
      submitInFlightRef.current = false;
      setSaving(false);
    }
  };

  const handleEditConfirm = async () => {
    if (!pendingEditPayload) {
      setEditConfirm({ open: false, delta: 0 });
      return;
    }
    try {
      setSaving(true);
      const updateResponse = await fetch(apiUrl(`/api/games/${editGameId}/update`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(pendingEditPayload),
      });
      const updateData = await updateResponse.json().catch(() => ({}));
      if (!updateResponse.ok) {
        console.error('[GAME_EDIT_CONFIRM_UPDATE_ERROR]', updateData);
        if (updateData.errorCode === 'INSUFFICIENT_FUNDS') {
          throw new Error(t('create.insufficientFunds.message'));
        }
        throw new Error(updateData.error || t('list.errors.save'));
      }
      setEditUpdatedAt(updateData?.game?.updatedAt || editUpdatedAt);
      setEditConfirm({ open: false, delta: 0 });
      setPendingEditPayload(null);
      setCreatedGameType('group');
      setCreatedGameModalOpen(true);
    } catch (error) {
      setErrorMessage(error.message || t('list.errors.save'));
    } finally {
      setSaving(false);
    }
  };

  const handleInsufficientFundsClose = () => {
    setInsufficientFundsOpen(false);
  };

  const handleCreatedGameModalClose = () => {
    const targetType = createdGameType || activeType;
    setCreatedGameModalOpen(false);
    setCreatedGameType(null);

    if (isEditMode) {
      if (editReturnTarget === 'future') {
        navigate('/dashboard/games/group', { search: '?tab=future' });
        return;
      }
      if (editReturnTarget === 'current') {
        navigate('/dashboard/games/group', { search: '?tab=active' });
        return;
      }
      navigate('/dashboard/games/group');
      return;
    }

    navigate(`/dashboard/games/${targetType}`, { search: '?tab=future' });
  };

  const handleEditCancel = () => {
    if (saving || editLoading) {
      return;
    }

    if (editReturnTarget === 'future') {
      navigate('/dashboard/games/group', { search: '?tab=future' });
      return;
    }

    if (editReturnTarget === 'current') {
      navigate('/dashboard/games/group', { search: '?tab=active' });
      return;
    }

    navigate('/dashboard/games/group');
  };

  const closeCardPreview = useCallback(() => {
    setCardPreview((prev) => ({ ...prev, open: false }));
  }, []);

  const handleOpenCardPreview = async () => {
    if (!form.deckId) {
      return;
    }

    setCardPreview({ open: true, loading: true, card: null, error: '' });
    try {
      const response = await fetch(apiUrl(`/api/decks/${form.deckId}/cards`), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || t('create.cardPreview.error'));
      }

      const data = await response.json();
      const cardsList = Array.isArray(data.cards) ? data.cards : Array.isArray(data) ? data : [];
      const normalizedCards = cardsList.map((card) => ({
        ...card,
        cellNumber: card.cellNumber ?? card.number ?? card.id,
        title: card.title ?? '',
        text: card.text ?? '',
      }));

      const previewCard = normalizedCards.find((card) => Number(card.cellNumber) === 1)
        || normalizedCards[0]
        || null;

      setCardPreview({
        open: true,
        loading: false,
        card: previewCard,
        error: previewCard ? '' : t('create.cardPreview.empty'),
      });
    } catch (previewError) {
      setCardPreview({
        open: true,
        loading: false,
        card: null,
        error: previewError.message || t('create.cardPreview.error'),
      });
    }
  };

  return (
    <div className="page-card">
      <div className="page-card-header balance-page__header">
        <div>
          {!isEditMode && <p className="page-caption">{t('create.page.caption')}</p>}
          <h1>{isEditMode ? t('create.page.editTitle') : t('create.page.title')}</h1>
          {!isEditMode && <p className="page-description">{t('create.page.description')}</p>}
        </div>
        <WalletBalanceSummary
          balance={walletBalance}
          loading={walletLoading}
          error={walletError}
        />
      </div>
      {!isEditMode && (
        <div className="type-tabs">
          {typeTabs.map((tab) => (
            <button
              key={tab.value}
              type="button"
              className={tab.value === activeType ? 'type-tab active' : 'type-tab'}
              onClick={() => {
                setActiveType(tab.value);
                setErrorMessage('');
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}
      {refsError && <p className="form-error">{refsError}</p>}
      <form className="game-form" onSubmit={handleSubmit}>
        <div className="game-setup-grid form-grid-limited">
          <label className="form-field form-field--wide">
            <span>{t('create.fields.title')}</span>
            <input
              type="text"
              value={form.title}
              onChange={(event) => setForm((prev) => ({ ...prev, title: event.target.value }))}
              placeholder={t('create.fields.titlePlaceholder')}
              required
            />
          </label>
          <div className="design-columns">
            <div className="design-column">
              <label className="form-field">
                <span>{t('create.fields.boardLanguage')}</span>
                {availableBoardLanguages.length > 1 ? (
                  <select
                    value={effectiveBoardLanguage}
                    onChange={(event) => handleBoardLanguageChange(event.target.value)}
                    disabled={refsLoading}
                  >
                    {availableBoardLanguages.map((language) => (
                      <option key={language} value={language}>
                        {getLocalizedLanguageLabel(language, t)}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className="static-input">
                    {availableBoardLanguages[0] ? getLocalizedLanguageLabel(availableBoardLanguages[0], t) : '—'}
                  </div>
                )}
              </label>
              <label className="form-field design-field">
                <span>{t('create.fields.board')}</span>
                <div className="field-inline">
                  <select
                    value={form.boardId || ''}
                    onChange={(event) => setForm((prev) => ({ ...prev, boardId: event.target.value }))}
                    disabled={refsLoading}
                    required
                  >
                    <option value="" disabled>
                      {t('create.fields.boardPlaceholder')}
                    </option>
                    {boardsBySelectedLanguage.map((board) => (
                      <option key={board.id} value={board.id}>
                        {board.name}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="icon-button preview-button"
                    onClick={() => setIsPreviewOpen(true)}
                    aria-label={t('create.fields.boardPreview')}
                    disabled={!form.boardId}
                  >
                    👁
                  </button>
                </div>
              </label>
            </div>
            <div className="design-column">
              <label className="form-field">
                <span>{t('create.fields.deckLanguage')}</span>
                {availableDeckLanguages.length > 1 ? (
                  <select
                    value={form.deckLanguage}
                    onChange={(event) => handleDeckLanguageChange(event.target.value)}
                    disabled={refsLoading}
                  >
                    {availableDeckLanguages.map((language) => (
                      <option key={language} value={language}>
                        {getLocalizedLanguageLabel(language, t)}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className="static-input">
                    {availableDeckLanguages[0] ? getLocalizedLanguageLabel(availableDeckLanguages[0], t) : '—'}
                  </div>
                )}
              </label>
              <label className="form-field design-field">
                <span>{t('create.fields.deck')}</span>
                <div className="field-inline">
                  {decksBySelectedLanguage.length > 1 ? (
                    <select
                      value={form.deckId}
                      onChange={(event) => setForm((prev) => ({ ...prev, deckId: event.target.value }))}
                      disabled={refsLoading}
                      required
                    >
                      {decksBySelectedLanguage.map((deck) => (
                        <option key={deck.id} value={deck.id}>
                          {deck.name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div className="static-input">{decksBySelectedLanguage[0]?.name || '—'}</div>
                  )}
                  <button
                    type="button"
                    className="icon-button preview-button"
                    onClick={handleOpenCardPreview}
                    aria-label={t('create.cardPreview.trigger')}
                    disabled={!form.deckId}
                  >
                    🂠
                  </button>
                </div>
              </label>
            </div>
          </div>
        </div>
        <div className="players-section players-section-limited">
          <h2>{t('create.players.title')}</h2>
          {activeType === 'individual' && <p className="players-hint">{t('create.players.individualHint')}</p>}
          {activeType === 'group' && (
            <p className="players-hint">{t('create.players.groupHint')}</p>
          )}
          {activeType === 'personal' && (
            <p className="players-hint">{t('create.players.personalHint')}</p>
          )}
          <div className="players-table">
            <div
              className={`players-table-header ${activeType === 'group' ? 'players-table-header--with-remove' : ''} ${
                activeType === 'personal' ? 'players-table-header--personal' : ''
              }`}
            >
              <span>{t('create.fields.playerName')}</span>
              <span className="field-label-with-info">
                {t('create.fields.diceType')}
                <InfoTooltip content={t('create.fields.diceTypeHint')}>
                  <button
                    type="button"
                    className="info-icon-button"
                    aria-label={t('create.fields.diceTypeInfoLabel')}
                  >
                    ℹ️
                  </button>
                </InfoTooltip>
              </span>
              <span className="players-header-stack">
                <span className="field-label-with-info">
                  {t('create.fields.resumeGame')}
                  <InfoTooltip content={t('create.fields.resumeGameHint')}>
                    <button
                      type="button"
                      className="info-icon-button"
                      aria-label={t('create.fields.resumeGameInfoLabel')}
                    >
                      ℹ️
                    </button>
                  </InfoTooltip>
                </span>
              </span>
              {activeType === 'group' && players.length > playerMin && <span aria-hidden="true" />}
            </div>
            <div className="players-list">
            {players.map((player, index) => (
              <div
                key={`player-${index}`}
              className={`player-row ${activeType === 'group' && players.length > playerMin ? 'player-row--with-remove' : ''} ${
                  activeType === 'personal' ? 'player-row--personal' : ''
                }`}
              >
                {activeType !== 'personal' && (
                  <label className="player-field">
                    <input
                      type="text"
                      aria-label={t('create.fields.playerName')}
                      value={player.name}
                      onChange={(event) => handlePlayerChange(index, 'name', event.target.value)}
                      placeholder={getDefaultPlayerName(index)}
                    />
                  </label>
                )}
                {activeType === 'personal' && (
                  <div className="player-field">
                    <div className="player-name-placeholder">
                      {user?.name || t('create.players.personalDefault')}
                    </div>
                  </div>
                )}
                <label className="player-field">
                  <select
                    aria-label={t('create.fields.diceType')}
                    value={player.diceType}
                    onChange={(event) => handlePlayerChange(index, 'diceType', event.target.value)}
                  >
                    {diceOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="player-field">
                  <input
                    type="number"
                    min="1"
                    step="1"
                    inputMode="numeric"
                    aria-label={t('create.fields.resumeGamePlaceholder')}
                    value={player.startCellInput}
                    onChange={(event) => {
                      handlePlayerChange(index, 'startCellInput', event.target.value);
                      setStartCellErrors((prev) => ({ ...prev, [index]: '' }));
                    }}
                    placeholder={t('create.fields.resumeGamePlaceholder')}
                  />
                  {startCellErrors[index] && <span className="field-error">{startCellErrors[index]}</span>}
                </label>
                {activeType === 'group' && players.length > playerMin && (
                  <button
                    type="button"
                    className="icon-button icon-button--danger player-remove-button"
                    onClick={() => removePlayer(index)}
                    disabled={isEditMode}
                    aria-label={t('create.players.remove')}
                  >
                    ✕
                  </button>
                )}
              </div>
            ))}
            </div>
          </div>
          {activeType === 'group' && (
            players.length < playerLimit && (
            <button
              type="button"
              className="secondary-button"
              onClick={addPlayer}
            >
              {t('create.players.add')}
            </button>
            )
          )}
        </div>
        {errorMessage && <p className="form-error">{errorMessage}</p>}
        <div className="form-footer">
          {isEditMode && (
            <button type="button" className="secondary-button" onClick={handleEditCancel} disabled={saving || editLoading}>
              {t('list.actions.cancelEdit')}
            </button>
          )}
          <button type="submit" className="primary-button" disabled={isCreateDisabled}>
            {saving ? t('create.buttons.creating') : isEditMode ? t('list.actions.saveEdit') : t('create.buttons.create')}
          </button>
          {costDisplay && (
            <div className={`game-price-placeholder ${!isCostConfigured ? 'game-price-placeholder--warning' : ''}`}>
              {costDisplay}
            </div>
          )}
        </div>
        {shouldShowTariffMissingMessage && (
          <p className="form-hint form-hint-warning">
            <Trans
              i18nKey="create.cost.supportMessage"
              components={{
                email: <a href="mailto:support@leelaspace.com">support@leelaspace.com</a>,
              }}
            />
          </p>
        )}
        {tariffsError && <p className="form-error">{tariffsError}</p>}
      </form>
      <CardPreviewModal
        open={cardPreview.open}
        card={
          cardPreview.loading || !cardPreview.card
            ? null
            : {
                cellNumber: cardPreview.card.cellNumber ?? 1,
                title: cardPreview.card.title,
                text: cardPreview.card.text,
                imageUrl: cardPreview.card.imageUrl || null,
              }
        }
        onClose={closeCardPreview}
        backgroundUrl={deckPreviewBackground}
        textStyle={getDeckCardTextStyle(selectedDeck?.textColor)}
        emptyMessage={
          cardPreview.loading
            ? t('create.cardPreview.loading')
            : cardPreview.error || t('create.cardPreview.error')
        }
        closeLabel={t('create.cardPreview.close')}
      />
      {isPreviewOpen && (
        <div className="modal-backdrop" onClick={() => setIsPreviewOpen(false)}>
          <div className="modal" onClick={(event) => event.stopPropagation()}>
            <div className="modal-header">
              <h3>{t('create.preview.title')}</h3>
              <button
                type="button"
                className="icon-button"
                onClick={() => setIsPreviewOpen(false)}
                aria-label={t('create.preview.close')}
              >
                ✕
              </button>
            </div>
            <div className="board-preview-frame">
              {previewImageUrl && !previewError ? (
                <img
                  src={previewImageUrl}
                  alt={selectedBoard?.name || t('create.preview.fallbackTitle')}
                  className="board-preview-image"
                  onError={() => setPreviewError(true)}
                />
              ) : (
                <div className="board-preview-placeholder">
                  <div className="board-preview-badge" aria-hidden="true">
                    {(selectedBoard?.name || t('create.preview.fallbackShort'))[0]?.toUpperCase()
                      || t('create.preview.fallbackShort')}
                  </div>
                  <div>
                    <p className="board-preview-title">{selectedBoard?.name || t('create.preview.fallbackTitle')}</p>
                    <p className="board-preview-subtitle">{t('create.preview.fallbackSubtitle')}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {insufficientFundsOpen && (
        <div className="modal-backdrop" onClick={handleInsufficientFundsClose}>
          <div className="modal" onClick={(event) => event.stopPropagation()}>
            <div className="modal-header">
              <h3>{t('create.insufficientFunds.title')}</h3>
              <button
                type="button"
                className="icon-button"
                onClick={handleInsufficientFundsClose}
                aria-label={t('create.insufficientFunds.close')}
              >
                ✕
              </button>
            </div>
            <p>{t('create.insufficientFunds.message')}</p>
            <div className="modal-actions">
              <button type="button" className="primary-button" onClick={handleInsufficientFundsClose}>
                {t('create.insufficientFunds.action')}
              </button>
            </div>
          </div>
        </div>
      )}
      {editConfirm.open && (
        <div className="modal-backdrop" role="alertdialog" aria-modal="true">
          <div className="modal confirm-modal edit-confirm-charge-modal" onClick={(event) => event.stopPropagation()}>
            <div className="modal-header">
              <h3>{t('list.actions.editConfirmChargeTitle')}</h3>
            </div>
            <p>{t('list.actions.editConfirmCharge', { amount: editConfirm.delta })}</p>
            <div className="modal-actions">
              <button type="button" className="ghost-button" onClick={() => setEditConfirm({ open: false, delta: 0 })} disabled={saving}>
                {t('list.confirmModal.actions.cancel')}
              </button>
              <button type="button" className="primary-button" onClick={handleEditConfirm} disabled={saving}>
                {t('list.confirmModal.actions.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
      {createdGameModalOpen && (
        <div className="modal-backdrop" role="alertdialog" aria-modal="true">
          <div className="modal create-game-success-modal" onClick={(event) => event.stopPropagation()}>
            <div className="modal-header">
              <h3>{t(isEditMode ? 'create.messages.editSuccess' : 'createdTitle')}</h3>
            </div>
            <div className="modal-actions">
              <button type="button" className="primary-button" onClick={handleCreatedGameModalClose}>
                {t('thanks', { ns: 'common' })}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateGamePage;
